import clsx from 'clsx'
import { Form, Formik, FormikHelpers } from 'formik'
import Router, { useRouter } from 'next/router'
import { useEffect, useReducer, useRef, useState } from 'react'

import Button from '@/atoms/Button/Button'
import {
  dataLayerFormSubmit,
  dataLayerInboundFormRedirectionEvent
} from '@/utils/functions/dataLayerEvents'
import {
  calendlyAllowedCountries,
  getUTMQueryParams,
  insertItemToArray
} from '@/utils/functions/utils'
import {
  budgetUSD,
  contentData,
  CustomFormSource,
  useCaseOptions
} from './fields'
import GettingStarted, { CustomGettingStarted } from './GettingStarted'
import {
  BudgetSelection,
  CategorySelection,
  StrategySolutionSelection,
  TimelineSelection,
  UseCaseSelection
} from './OrderingForms'
import {
  FormPages,
  FormValues,
  FORM_STEPS,
  initialValues,
  validationSchema,
  customFormSchema,
  CustomForm,
  initialCustomFormValues
} from './schema'

/* assets */
import { ReactComponent as ArrowLeftIcon } from 'public/assets/icons/arrow-left.svg'
import { SESSION_STORAGE_FORM_TYPE } from 'pages/book-a-demo'

interface Props {
  formSource: CustomFormSource | null
}

type ReducerState = {
  currentStep: FormPages
  currentStepIndex: number
  steps: FormPages[]
  isLoading: boolean
  isFormCompleted: boolean
}

const initialFormState: ReducerState = {
  currentStep: 'getting-started',
  steps: FORM_STEPS,
  currentStepIndex: 0,
  isLoading: false,
  isFormCompleted: false
}

type ReducerAction =
  | { type: 'form-step'; step: FormPages }
  | { type: 'form-step-next' }
  | { type: 'form-step-back' }
  | { type: 'form-loading'; status: boolean }
  | {
      type: 'form-use_cases-selection'
      value: string[]
      helpers: FormikHelpers<FormValues>
    }
  // `form-type-us-lp` will only have one step
  | { type: `form-type-${CustomFormSource}` }

function reducer(state: ReducerState, action: ReducerAction): ReducerState {
  switch (action.type) {
    case 'form-step': {
      return {
        ...state,
        currentStep: action.step,
        currentStepIndex: state.steps.indexOf(action.step)
      }
    }
    case 'form-step-next': {
      const currentStepIndex = state.steps.indexOf(state.currentStep)
      const isLastStep = currentStepIndex === state.steps.length - 1
      const nextStep = state.steps[currentStepIndex + 1]

      if (isLastStep) {
        return { ...state, isFormCompleted: true }
      }
      return {
        ...state,
        currentStepIndex: currentStepIndex + 1,
        currentStep: nextStep
      }
    }
    case 'form-step-back': {
      const currentStepIndex = state.steps.indexOf(state.currentStep)
      const isFirstStep = currentStepIndex === 0
      const prevStep = state.steps[currentStepIndex - 1]

      if (isFirstStep) {
        return state
      }
      return {
        ...state,
        currentStepIndex: currentStepIndex - 1,
        currentStep: prevStep
      }
    }
    case 'form-loading': {
      return { ...state, isLoading: action.status }
    }
    case 'form-use_cases-selection': {
      const showContentCategory = useCaseOptions.some(
        option =>
          option.metadata.showContentCategory &&
          action.value.includes(option.key)
      )
      const showStrategySolution = useCaseOptions.some(
        option =>
          option.metadata.showStrategySolution &&
          action.value.includes(option.value)
      )

      let updatedSteps = [...state.steps]

      if (showContentCategory) {
        updatedSteps = !updatedSteps.includes('category')
          ? insertItemToArray(
              updatedSteps,
              updatedSteps.indexOf('use_cases') + 1,
              'category'
            )
          : updatedSteps
      } else {
        updatedSteps = updatedSteps.filter(step => step !== 'category')
        action.helpers.setFieldValue('content_category', '')
      }

      if (showStrategySolution) {
        updatedSteps = !updatedSteps.includes('strategy_solution')
          ? insertItemToArray(
              updatedSteps,
              updatedSteps.length,
              'strategy_solution'
            )
          : updatedSteps
      } else {
        updatedSteps = updatedSteps.filter(step => step !== 'strategy_solution')
        action.helpers.setFieldValue('strategy_solution', '')
      }

      return { ...state, steps: updatedSteps }
    }
    case 'form-type-newsletter':
      return { ...state, steps: [state.steps[0]] }
    default:
      return initialFormState
  }
}

const SESSION_STORAGE_FORM_KEY = `inbound-form-values`

function OrderFormRenderer({ formSource }: Props): JSX.Element {
  const router = useRouter()

  const [state, dispatch] = useReducer(reducer, initialFormState)
  const [initialFormValues, setInitialFormValues] = useState<FormValues>({
    ...initialValues
  })
  const initialPageState = useRef(false)

  const page = router.query.page?.toString()

  useEffect(() => {
    if (initialPageState.current) return

    if (FORM_STEPS.includes(page as FormPages)) {
      dispatch({ type: 'form-step', step: page as FormPages })
      initialPageState.current = true
    }

    if (formSource) {
      dispatch({ type: `form-type-${formSource}` })
    }
  }, [page, formSource, router.query])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    // preserve utm query params if available
    const query = {
      page: state.currentStep,
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined
    }
    // JSON.stringify will remove undefined values
    Router.replace({ query: JSON.parse(JSON.stringify(query)) })

    // hide carousel container on mobile screen after first step
    if (state.currentStep !== 'getting-started') {
      document.body.style.setProperty('--order-carousel-display', 'none')
    }
  }, [state.currentStep])

  useEffect(() => {
    // set form values from sessionStorage if available
    const stringValues = sessionStorage.getItem(SESSION_STORAGE_FORM_KEY)
    if (page !== 'getting-started' && stringValues !== null) {
      const sessionStorageFormValues = JSON.parse(stringValues)
      setInitialFormValues(prevValue => ({
        ...prevValue,
        ...sessionStorageFormValues
      }))
    }
  }, [page, state.currentStep])

  type HandleMalichimpSubmitParams = {
    first_name: string
    last_name: string
    company: string
    numemployees: string
    email: string
    country: string
    phone: {
      number: string
      extension: string
    }
    est_monthly_content_budget: string
    use_cases: string
    content_category: string
    requirement_timeline: string
    strategy_solution: string
    source?: string
  }

  const handleMailChimpSubmit = async (values: HandleMalichimpSubmitParams) => {
    const utmParams = getUTMQueryParams()
    const response = await fetch('/api/forms/order/', {
      method: 'POST',
      body: JSON.stringify({
        ...values,
        ...utmParams,
        source: '/book-a-demo/',
        status: 'subscribed'
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json()

    if (response.ok) {
      dataLayerFormSubmit({
        formName: contentData.default?.formName ?? '',
        formStep: state.currentStep,
        formValues: values
      })
    } else {
      const defaultMessage =
        'We are not sure what went wrong. You can write to use at support@peppercontent.io for further support'
      alert(data.message ?? defaultMessage)
      throw new Error(data.message ?? defaultMessage)
    }
  }

  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    sessionStorage.setItem(SESSION_STORAGE_FORM_KEY, JSON.stringify(values))
    const { use_cases, content_category } = values
    // mailchimp expects uses cases and categories to be a single value
    const newUseCases = use_cases.join(' | ')
    const newContentCategory = content_category.join(' ')

    try {
      dispatch({ type: 'form-loading', status: true })

      await handleMailChimpSubmit({
        ...values,
        use_cases: newUseCases,
        content_category: newContentCategory
      })

      // condition to add `category` step
      if (state.currentStep === 'use_cases') {
        dispatch({
          type: 'form-use_cases-selection',
          value: values.use_cases,
          helpers
        })
      }

      dispatch({ type: 'form-step-next' })

      if (state.steps.length === state.currentStepIndex + 1) {
        handleJourneyRedirection(values)
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch({ type: 'form-loading', status: false })
    }
  }

  const handleCustomSubmit = async (values: CustomForm) => {
    if (!formSource) return
    try {
      const result = await fetch('/api/forms/resources/', {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          status: 'subscribed'
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const data = await result.json()

      if (!result.ok) {
        alert(data.message)
        return
      }

      if (result.ok) {
        dataLayerFormSubmit({
          formName: contentData[formSource]?.formName ?? '',
          formValues: values
        })
        sessionStorage.removeItem(SESSION_STORAGE_FORM_TYPE)
        router.push({
          pathname: `/thank-you/`,
          query: { param: formSource }
        })
      } else {
        const defaultMessage =
          'We are not sure what went wrong. You can write to use at support@peppercontent.io for further support'
        alert(data.message ?? defaultMessage)
        throw new Error(data.message ?? defaultMessage)
      }
    } catch (e) {
      alert('Please try again')
    } finally {
      dispatch({ type: 'form-loading', status: false })
    }
  }

  const handleJourneyRedirection = (values: FormValues) => {
    const params = new URLSearchParams({
      email: values.email,
      first_name: values.first_name,
      last_name: values.last_name,
      country: values.country
    })

    // self-serve journey
    if (
      !calendlyAllowedCountries.includes(values.country) ||
      values.est_monthly_content_budget === budgetUSD[0].value
    ) {
      dataLayerInboundFormRedirectionEvent({ destination: 'self-serve' })
      router.push('/thank-you/')
      return
    }

    // calendly page journey

    dataLayerInboundFormRedirectionEvent({ destination: 'calendly' })
    window.location.href = `/book-a-demo/loading/?${params.toString()}`
  }

  const handleBack = () => {
    dispatch({ type: 'form-step-back' })
  }

  const renderForm = (page: FormPages) => {
    const commonProps = {
      currentStep: state.currentStepIndex,
      maxSteps: state.steps.length
    }
    switch (page) {
      case 'getting-started':
        return (
          <GettingStarted
            title={contentData['default']?.title}
            description={contentData['default']?.description}
            ctaTitle={contentData['default']?.ctaTitle}
          />
        )
      case 'budget':
        return <BudgetSelection {...commonProps} />
      case 'use_cases':
        return <UseCaseSelection {...commonProps} />
      case 'category':
        return <CategorySelection {...commonProps} />
      case 'requirement_timeline':
        return <TimelineSelection {...commonProps} />
      case 'strategy_solution':
        return <StrategySolutionSelection {...commonProps} />
      default:
        return `page not found ${page}`
    }
  }

  return (
    <>
      {formSource ? (
        <Formik<CustomForm>
          enableReinitialize={true}
          initialValues={initialCustomFormValues}
          onSubmit={handleCustomSubmit}
          validationSchema={customFormSchema}
        >
          <Form>
            <CustomGettingStarted
              title={contentData[formSource]?.title}
              description={contentData[formSource]?.description}
              ctaTitle={contentData[formSource]?.ctaTitle}
            />
          </Form>
        </Formik>
      ) : (
        <Formik<FormValues>
          enableReinitialize={true}
          initialValues={initialFormValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema[state.currentStep]}
        >
          <Form>
            <div className="mb-12">{renderForm(state.currentStep)}</div>

            <div
              className={clsx(
                'flex item-center justify-between | bg-white | border-t border-gray-20 sm:border-none | fixed left-0 right-0 bottom-0 sm:static | py-3 px-6 sm:p-0 | w-full',
                state.currentStep === 'getting-started' && 'hidden'
              )}
            >
              <button
                className={clsx(
                  'bg-gray-10 | flex items-center justify-center | rounded-full | w-10 h-10',
                  state.currentStepIndex === 1 && 'hidden'
                )}
                onClick={handleBack}
                type="button"
              >
                <ArrowLeftIcon />
              </button>
              <Button
                size="small"
                className="ml-auto"
                htmlType="submit"
                loading={state.isLoading}
              >
                Next
              </Button>
            </div>
          </Form>
        </Formik>
      )}
    </>
  )
}

export default OrderFormRenderer
